import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Select,
  Radio,
  Checkbox,
  Button,
  Space,
  Typography,
  Card,
  message
} from 'antd';
import { SaveOutlined, CloseOutlined } from '@ant-design/icons';
import {
  CreateQuestionarioDTO,
  AbordagemType,
  Area,
  Cliente
} from '../../../types/dashboard/mapeamentos.types';

const { Title } = Typography;
const { Option } = Select;

interface QuestionarioFormProps {
  onSubmit: (data: CreateQuestionarioDTO) => Promise<void>;
  onCancel: () => void;
  initialData?: Partial<CreateQuestionarioDTO>;
  clientes: Cliente[];
  areas: Area[];
  loading?: boolean;
}

const QuestionarioForm: React.FC<QuestionarioFormProps> = ({
  onSubmit,
  onCancel,
  initialData,
  clientes,
  areas,
  loading = false
}) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (initialData) {
      form.setFieldsValue(initialData);
    }
  }, [initialData, form]);

  const handleSubmit = async (values: CreateQuestionarioDTO) => {
    setSubmitting(true);
    try {
      await onSubmit(values);
      message.success('Questionário salvo com sucesso!');
    } catch (error) {
      message.error('Erro ao salvar questionário');
      console.error('Erro ao salvar questionário:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card
      title={
        <div className="flex justify-between items-center">
          <Title level={3} style={{ margin: 0 }}>
            {initialData ? 'Editar Questionário' : 'Novo Questionário'}
          </Title>
          <Button
            type="text"
            icon={<CloseOutlined />}
            onClick={onCancel}
          />
        </div>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          abordagem: AbordagemType.DETALHADA,
          areas: []
        }}
      >
        <Form.Item
          label="Nome do Questionário"
          name="nome"
          rules={[
            { required: true, message: 'Nome é obrigatório' },
            { max: 100, message: 'Nome deve ter no máximo 100 caracteres' }
          ]}
        >
          <Input placeholder="Digite o nome do questionário" />
        </Form.Item>

        <Form.Item
          label="Cliente"
          name="cliente_id"
        >
          <Select
            placeholder="Selecione um cliente (opcional)"
            allowClear
          >
            {clientes.map(cliente => (
              <Option key={cliente._id} value={cliente._id}>
                {cliente.nome}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="Abordagem"
          name="abordagem"
        >
          <Radio.Group>
            <Space direction="vertical">
              <Radio value={AbordagemType.DETALHADA}>Detalhada</Radio>
              <Radio value={AbordagemType.SIMPLIFICADA}>Simplificada</Radio>
            </Space>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          label="Áreas de Interesse"
          name="areas"
        >
          <Checkbox.Group>
            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
              <Space direction="vertical">
                {areas.map(area => (
                  <Checkbox key={area._id} value={area._id}>
                    <span>{area.description}</span>
                    {area.padrao && (
                      <span style={{ marginLeft: '8px', fontSize: '12px', color: '#1890ff' }}>
                        (Padrão)
                      </span>
                    )}
                  </Checkbox>
                ))}
              </Space>
            </div>
          </Checkbox.Group>
        </Form.Item>

        <Form.Item>
          <Space>
            <Button onClick={onCancel}>
              Cancelar
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={submitting || loading}
              icon={<SaveOutlined />}
            >
              Salvar
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default QuestionarioForm;